import React from 'react';
import useTranslation from '../../hooks/useTranslation';
import { formatDateTime } from '../../utils/dateFormatting';

const formatDateTimeFallback = (dateStr, timeStr) => formatDateTime(dateStr, timeStr) || 'Not scheduled';

const hasAppointmentWithin5Minutes = (patient) => {
  if (!patient.next_session || !patient.next_session_time) return false;
  try {
    const [hours, minutes] = patient.next_session_time.split(':');
    const appointmentDate = new Date(patient.next_session);
    appointmentDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    const now = new Date();
    const timeDiff = appointmentDate.getTime() - now.getTime();
    return timeDiff >= 0 && timeDiff <= 5 * 60 * 1000;
  } catch {
    return false;
  }
};

export default function PatientListPanel({
  patients,
  loading,
  searchQuery,
  onSearchChange,
  onStartSession,
  onViewReport,
  onViewHistory,
  refreshTrigger
}) {
  const { t } = useTranslation();

  const filteredPatients = React.useMemo(() => {
    let filtered = patients;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = patients.filter((p) => {
        const name = (p.name || "").toLowerCase();
        const contact = (p.contact || "").toLowerCase();
        const status = (p.status || "").toLowerCase();
        const age = (p.age || "").toString().toLowerCase();
        return name.includes(query) || contact.includes(query) || status.includes(query) || age.includes(query);
      });
    }

    return filtered.sort((a, b) => {
      const aHasUrgent = hasAppointmentWithin5Minutes(a);
      const bHasUrgent = hasAppointmentWithin5Minutes(b);

      if (aHasUrgent && !bHasUrgent) return -1;
      if (!aHasUrgent && bHasUrgent) return 1;

      if (a.next_session && b.next_session) {
        const dateA = new Date(a.next_session + ' ' + (a.next_session_time || '00:00'));
        const dateB = new Date(b.next_session + ' ' + (b.next_session_time || '00:00'));
        return dateA - dateB;
      }
      if (a.next_session) return -1;
      if (b.next_session) return 1;
      return 0;
    });
  }, [patients, searchQuery, refreshTrigger]);

  if (loading) {
    return <div className="loading-state">{t('Loading...')}</div>;
  }

  if (patients.length === 0) {
    return <div className="empty-state">{t('No patients assigned yet.')}</div>;
  }

  return (
    <>
      <div style={{ marginBottom: 16, position: 'relative' }}>
        <input
          type="text"
          placeholder={t('Search patients by name, contact, status, or age...')}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{
            width: '100%',
            padding: '10px',
            paddingRight: searchQuery ? '40px' : '10px',
            fontSize: '14px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            boxSizing: 'border-box'
          }}
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange("")}
            style={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px',
              color: '#6b7280',
              padding: '4px 8px'
            }}
            title="Clear search"
          >
            ×
          </button>
        )}
      </div>
      {filteredPatients.length === 0 ? (
        <div className="empty-state">
          {patients.length === 0
            ? t("No patients assigned yet.")
            : t('No patients found matching') + ` "${searchQuery}"`}
        </div>
      ) : (
        <div className="patients-grid">
          {filteredPatients.map((patient) => {
            const isUrgent = hasAppointmentWithin5Minutes(patient);
            return (
              <div
                key={patient.id}
                className={`patient-card ${isUrgent ? 'patient-card-urgent' : ''}`}
                style={isUrgent ? {
                  border: '3px solid #f59e0b',
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                  background: 'linear-gradient(135deg, #fef3c7 0%, #ffffff 100%)',
                  animation: 'pulse-urgent 2s ease-in-out infinite'
                } : {}}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <h3 style={{ margin: 0 }}>{patient.name}</h3>
                  <button
                    className="btn-secondary"
                    onClick={() => onViewHistory(patient)}
                    style={{
                      fontSize: '12px',
                      padding: '4px 10px',
                      minWidth: 'auto',
                      background: '#ffffff',
                      color: '#000000',
                      border: '1px solid #d1d5db',
                      fontWeight: '600'
                    }}
                  >
                    {t('History')}
                  </button>
                </div>
                {isUrgent && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      background: '#f59e0b',
                      color: '#ffffff',
                      padding: '6px 12px',
                      borderRadius: '16px',
                      fontSize: '12px',
                      fontWeight: '600',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span>⏰</span>
                      <span>{t('Appointment Soon')}</span>
                    </div>
                  </div>
                )}
                <p>{t('Age')}: {patient.age || t('N/A')}</p>
                <p>{t('Contact')}: {patient.contact || t('N/A')}</p>
                <p>{t('Status')}: <span className={`badge ${patient.status === 'High Risk' ? 'badge-danger' : patient.status === 'Moderate Risk' ? 'badge-warning' : 'badge-success'}`}>
                  {patient.status ? t(patient.status) : t('No Data')}
                </span></p>
                <p>{t('Last Session')}: {patient.last_session ? formatDateTimeFallback(patient.last_session, patient.last_session_time) : t('None')}</p>
                <p>{t('Next Session')}: {patient.next_session ? formatDateTimeFallback(patient.next_session, patient.next_session_time) : t('Not scheduled')}</p>
                <div className="patient-card-actions">
                  <button
                    className="btn-primary"
                    onClick={() => onStartSession(patient)}
                  >
                    {t('Start Session')}
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => onViewReport(patient)}
                    style={{
                      background: '#ffffff',
                      color: '#000000',
                      border: '1px solid #d1d5db',
                      fontWeight: '600'
                    }}
                  >
                    {t('Report')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
